/**
 * Membership status copy for member app, admin, and merchant redemption picker.
 * Avoids "Inactive · Expires <future date>" when access is suspended for payment.
 */

function noteText(note) {
  if (!note || typeof note !== "object") return "";
  return Object.values(note)
    .map((v) => (typeof v === "string" ? v : ""))
    .join(" ")
    .toLowerCase();
}

export function noteIndicatesPaymentIssue(note) {
  const text = noteText(note);
  if (!text) return false;
  return (
    text.includes("payment") ||
    text.includes("past due") ||
    text.includes("past_due") ||
    text.includes("unpaid") ||
    text.includes("no payment") ||
    text.includes("invoice")
  );
}

/** Portal row marked deleted while access is still on (cancel at period end). */
export function isPendingCancellation(subscription) {
  if (!subscription?.active) return false;
  return (
    subscription.deleted === "yes" ||
    subscription.deleted === true ||
    subscription.status === "deleted"
  );
}

/**
 * Inactive with a future validUntil often means Stripe period end is still in the
 * future while active=false (failed payment or never completed checkout).
 */
export function isPaymentSuspendedMembership(subscription) {
  if (!subscription || subscription.active !== false) return false;
  const now = Date.now();
  const from = subscription.validFromDateUTC
    ? new Date(subscription.validFromDateUTC).getTime()
    : null;
  if (from && from > now) return false;
  const until = subscription.validUntilDateUTC
    ? new Date(subscription.validUntilDateUTC).getTime()
    : null;
  if (!until || until <= now) return false;
  if (noteIndicatesPaymentIssue(subscription.note)) return true;
  if (subscription.stripeId && subscription.deleted !== "yes") return true;
  if (subscription.paymentRequired === true) return true;
  return false;
}

/**
 * @returns {{ statusLabel: string, expirySuffix: string|null, expiryMuted: boolean }}
 */
export function getMembershipStatusPresentation(subscription, { isInactiveTab = false } = {}) {
  const now = Date.now();
  const until = subscription?.validUntilDateUTC
    ? new Date(subscription.validUntilDateUTC)
    : null;
  const untilValid = until && !Number.isNaN(until.getTime()) && until.getFullYear() !== 1970;
  if (isInactiveTab && subscription?.validFromDateUTC) {
    const fromDate = new Date(subscription.validFromDateUTC);
    if (!Number.isNaN(fromDate.getTime()) && fromDate.getTime() > now) {
      return { statusLabel: "Scheduled", expirySuffix: null, expiryMuted: false };
    }
  }

  if (isPendingCancellation(subscription)) {
    return { statusLabel: "Pending cancellation", expirySuffix: null, expiryMuted: false };
  }

  if (subscription?.active) {
    return { statusLabel: "Active", expirySuffix: null, expiryMuted: false };
  }

  if (isPaymentSuspendedMembership(subscription)) {
    return {
      statusLabel: "Payment required",
      expirySuffix: untilValid
        ? `Paid-through date ${until.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
          })} — not covered until payment succeeds`
        : "Membership suspended until payment succeeds",
      expiryMuted: true,
    };
  }

  if (untilValid && until.getTime() > now) {
    return {
      statusLabel: "Inactive",
      expirySuffix: `Expires ${until.toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })}`,
      expiryMuted: false,
    };
  }

  return { statusLabel: "Inactive", expirySuffix: null, expiryMuted: false };
}
